import type { ProjectDetail } from "@esi/shared";
import { SquarePen } from "lucide-react";
import { type RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { cn } from "@/lib/utils";
import { ConversationList } from "../conversation/ConversationList.tsx";
import { lastProject } from "../home/last-project.ts";
import { ProjectMemory } from "../memory/ProjectMemory.tsx";
import { useProjectMemories } from "../memory/queries.ts";
import {
  Link,
  type ProjectSettingsGroup,
  type ProjectTab,
  projectConversationPath,
  projectHomePath,
  projectSettingsPath,
  type Route,
} from "../router.tsx";
import { hashTarget } from "../settings-shell/setting-anchors.ts";
import { NotFoundPage } from "../shell/Placeholders.tsx";
import { TaskDetailPage } from "../tasks/TaskDetailPage.tsx";
import { ProjectTasks } from "../tasks/TaskList.tsx";
import { ResidencyNote } from "../trust/ResidencyNote.tsx";
import { Wiki } from "../wiki/WikiView.tsx";
import { can } from "./capabilities.ts";
import { HeaderAsk } from "./HeaderAsk.tsx";
import { ProjectOverview } from "./home/ProjectOverview.tsx";
import { Files, FrozenMark, useProjectMeta, WithProject } from "./ProjectView.tsx";
import { ProjectSettings } from "./settings/ProjectSettings.tsx";

type HomeTab = ProjectTab | "settings";
type WikiRoute = Extract<
  Route,
  {
    name:
      | "wiki"
      | "wikiNew"
      | "wikiEdit"
      | "wikiDraft"
      | "wikiChange"
      | "wikiHistory"
      | "wikiCompare";
  }
>;

/**
 * A project's home (spec `web-shell` "Project home page"): its header, then the tabs Overview,
 * Wiki, Sources (the `files` tab), Conversations, Tasks, Memory for users who may view project
 * memories and Settings for users who may view members. The home opens on Overview, at the bare
 * project URL; the Wiki tab is at `/wiki`. Each tab is a link with its own URL; after navigating,
 * focus moves to the tab panel. The header's "Ask about this project" field (a pen control on
 * phones) starts a conversation on every tab, unless the project is frozen.
 */
export function ProjectHome(props: {
  projectId: string;
  tab: HomeTab;
  taskId?: string;
  wiki?: WikiRoute;
  settingsGroup?: ProjectSettingsGroup;
}) {
  return (
    <WithProject projectId={props.projectId}>
      {(project) =>
        props.tab === "settings" && !can(project, "project.view_members") ? (
          <NotFoundPage />
        ) : (
          <Home
            project={project}
            tab={props.tab}
            taskId={props.taskId}
            wiki={props.wiki}
            settingsGroup={props.settingsGroup}
          />
        )
      }
    </WithProject>
  );
}

function Home({
  project,
  tab,
  wiki,
  settingsGroup,
  taskId,
}: {
  project: ProjectDetail;
  tab: HomeTab;
  taskId?: string;
  wiki?: WikiRoute;
  settingsGroup?: ProjectSettingsGroup;
}) {
  const { t } = useTranslation();
  const meta = useProjectMeta(project);
  const ProjectTitle = tab === "settings" || tab === "tasks" ? "p" : "h1";
  const panel = useRef<HTMLElement>(null);
  const first = useRef(true);
  const strip = useRef<HTMLUListElement>(null);
  const more = useStripOverflow(strip, tab);
  useEffect(() => {
    lastProject.write(project.id);
  }, [project.id]);
  useDocumentTitle(
    tab === "wiki" || tab === "settings" || tab === "tasks" ? undefined : project.name,
    tab !== "wiki" && tab !== "settings" && tab !== "tasks",
  );
  // Tabs focus their panel; a settings deep link keeps its shared anchor focus.
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (tab !== "settings" || hashTarget() === null) {
      if (panel.current) {
        panel.current.scrollTop = 0;
        panel.current.focus();
      }
    }
  }, [tab]);
  // The review queue's size beside the tab, for owners and managers.
  const memoryQueue = useProjectMemories(project.id, "pending", "", {
    enabled: can(project, "memory.manage"),
  });
  const pendingCount = can(project, "memory.manage") ? (memoryQueue.data?.pendingCount ?? 0) : 0;
  const tabs: {
    tab: HomeTab;
    href: string;
    label: string;
    count?: number;
    countLabel?: string;
  }[] = [
    { tab: "home", href: projectHomePath(project.id), label: t("projectHome.tabs.home") },
    { tab: "wiki", href: projectHomePath(project.id, "wiki"), label: t("project.tabs.wiki") },
    { tab: "files", href: projectHomePath(project.id, "files"), label: t("project.tabs.files") },
    {
      tab: "conversations",
      href: projectHomePath(project.id, "conversations"),
      label: t("projectHome.tabs.conversations"),
    },
    { tab: "tasks", href: projectHomePath(project.id, "tasks"), label: t("tasks.title") },
    ...(can(project, "memory.view")
      ? [
          {
            tab: "memory" as const,
            href: projectHomePath(project.id, "memory"),
            label: t("projectHome.tabs.memory"),
            ...(pendingCount > 0 && {
              count: pendingCount,
              countLabel: t("memory.project.tabPending", { count: pendingCount }),
            }),
          },
        ]
      : []),
    // Guests see no members and no settings: the page would be not found for them.
    ...(can(project, "project.view_members")
      ? [
          {
            tab: "settings" as const,
            href: projectSettingsPath(project.id),
            label: t("projectHome.tabs.settings"),
          },
        ]
      : []),
  ];
  const newConversationHref = project.frozenAt
    ? undefined
    : projectConversationPath(project.id, "new");
  // The header's way to start a conversation, unless frozen: the one-line ask field from `sm`,
  // the named 44px pen control on phones.
  const newConversation = newConversationHref ? (
    <>
      <HeaderAsk project={project} />
      <Button
        asChild
        size="sm"
        variant="ghost"
        className="size-11 text-fg-secondary hover:text-fg sm:hidden"
      >
        <Link href={newConversationHref}>
          <SquarePen aria-hidden="true" strokeWidth={1.75} />
          <span className="sr-only">{t("projectHome.newConversation")}</span>
        </Link>
      </Button>
    </>
  ) : null;

  return (
    <div data-project-shell className="flex min-h-0 w-full flex-1 flex-col">
      <header data-esi-content="projects" className="flex shrink-0 flex-col gap-1 px-4 py-2">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
            <ProjectTitle className="truncate text-[15px] font-semibold tracking-tight text-fg">
              {project.name}
            </ProjectTitle>
            {project.frozenAt ? <FrozenMark /> : null}
          </div>
          {newConversation}
        </div>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <p className="text-xs text-fg-muted">{meta}</p>
          <ResidencyNote value={project.residency?.value} codeExecution={project.codeExecution} />
        </div>
      </header>
      {project.frozenAt && tab !== "settings" ? (
        <p className="shrink-0 px-4 py-2 text-sm text-fg-secondary">{t("project.frozen")}</p>
      ) : null}
      <div className="shrink-0 border-b border-line px-4">
        <nav aria-label={t("projectHome.tabsLabel")} className="relative min-w-0">
          <ul ref={strip} className="-mb-px flex gap-4 overflow-x-auto sm:gap-5">
            {tabs.map((item) => {
              const current = item.tab === tab;
              return (
                <li key={item.tab}>
                  <Link
                    href={item.href}
                    aria-current={current ? "page" : undefined}
                    className={cn(
                      "inline-flex h-10 items-center border-b-2 text-sm font-medium whitespace-nowrap transition-colors duration-(--duration-fast)",
                      current
                        ? "border-fg text-fg"
                        : "border-transparent text-fg-secondary hover:text-fg",
                    )}
                  >
                    {item.label}
                    {item.count ? (
                      <>
                        <span
                          aria-hidden="true"
                          className="ml-1.5 inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-surface-selected px-1 text-[11px] font-semibold text-fg-secondary tabular-nums"
                        >
                          {item.count}
                        </span>
                        <span className="sr-only">, {item.countLabel}</span>
                      </>
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ul>
          {/* More tabs past the right edge (phones, French): a fade says the strip scrolls. */}
          {more ? (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-linear-to-l from-surface"
            />
          ) : null}
        </nav>
      </div>
      <section
        ref={panel}
        tabIndex={-1}
        aria-label={tabs.find((x) => x.tab === tab)?.label}
        data-scroll-owner={tab === "wiki" ? undefined : "page"}
        className={cn(
          "flex min-h-0 flex-1 flex-col outline-none",
          tab !== "wiki" && "gap-6 overflow-y-auto px-4 py-6",
        )}
      >
        {tab === "settings" ? (
          // SettingsShell sets no width: this centred column gives it the other settings screens'
          // width (`Page`'s default `max-w-5xl`), so it matches the Backoffice settings.
          <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
            <ProjectSettings project={project} group={settingsGroup ?? null} />
          </div>
        ) : tab === "conversations" ? (
          <ConversationList projectId={project.id} newConversationHref={newConversationHref} />
        ) : tab === "tasks" ? (
          taskId ? (
            <TaskDetailPage key={taskId} project={project} taskId={taskId} />
          ) : (
            <ProjectTasks project={project} />
          )
        ) : tab === "home" ? (
          <ProjectOverview project={project} />
        ) : tab === "files" ? (
          <Files project={project} />
        ) : tab === "memory" ? (
          can(project, "memory.view") ? (
            <ProjectMemory project={project} />
          ) : (
            <Notice>{t("memory.project.notAllowed")}</Notice>
          )
        ) : wiki ? (
          <Wiki project={project} route={wiki} embedded />
        ) : (
          <Wiki
            project={project}
            route={{ name: "wiki", projectId: project.id, pageId: null, tab: "pages" }}
            embedded
          />
        )}
      </section>
    </div>
  );
}

/** The width of the strip's right-edge fade (`w-10`). */
const FADE = 40;

/**
 * The tab strip scrolls sideways when its tabs do not fit (phones, French): keeps the current
 * tab in view on load and on navigation, and says whether more tabs sit past the right edge.
 */
function useStripOverflow(strip: RefObject<HTMLUListElement | null>, tab: HomeTab) {
  const [more, setMore] = useState(false);
  const previousMore = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-runs on a tab change
  useEffect(() => {
    const el = strip.current;
    if (!el) {
      return;
    }
    const current = el.querySelector<HTMLElement>('[aria-current="page"]');
    if (current) {
      // In view, clear of the edge fade (40px) unless it is the last tab.
      const frame = el.getBoundingClientRect();
      const box = current.getBoundingClientRect();
      const last = el.lastElementChild?.contains(current) ?? false;
      const edge = frame.right - (last ? 0 : FADE);
      if (box.left < frame.left) {
        el.scrollLeft -= frame.left - box.left;
      } else if (box.right > edge) {
        el.scrollLeft += box.right - edge;
      }
    }
    let mounted = true;
    const update = () => {
      if (!mounted) {
        return;
      }
      const next = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      if (previousMore.current !== next) {
        previousMore.current = next;
        setMore(next);
      }
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    // The strip's box, and its tabs' (a count badge, fonts, a language change widen them).
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    resize?.observe(el);
    for (const item of el.children) {
      resize?.observe(item);
    }
    return () => {
      mounted = false;
      el.removeEventListener("scroll", update);
      resize?.disconnect();
    };
  }, [strip, tab]);
  return more;
}
